import React, { useCallback, useState } from 'react';
import { TagInput, TagInputProps } from '@blueprintjs/core';

interface CustomTagInputProps extends TagInputProps {
  onBlur?: (values: string[]) => void;
}

const CustomTagInput: React.FC<CustomTagInputProps> = ({
  onBlur,
  values: initialTags = [],
  ...otherProps
}) => {
  const [inputValue, setInputValue] = useState('');
  const [tags, setTags] = useState<string[]>(
    initialTags.map((tag) => String(tag)).filter((tag) => tag !== 'undefined'),
  );

  const handleInputBlur = useCallback(() => {
    const value = inputValue.trim();
    if (value) {
      const newTags = value.split(',').map((tag) => tag.trim());
      setTags((prevTags) => [...prevTags, ...newTags]);
      if (onBlur) {
        onBlur([...tags, ...newTags]);
      }
      setInputValue('');
    }
  }, [inputValue, onBlur, tags]);

  const handleInputChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      setInputValue(event.target.value);
    },
    [],
  );

  const handleKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLInputElement>) => {
      if (event.key === 'Tab' || event.key === 'Enter') {
        handleInputBlur();
      }
    },
    [handleInputBlur],
  );

  const handleTagRemove = useCallback(
    (value: React.ReactNode, index: number) => {
      setTags((prevTags) => {
        const newTags = [...prevTags];
        newTags.splice(index, 1);
        return newTags;
      });
    },
    [],
  );

  return (
    <TagInput
      {...otherProps}
      inputProps={{
        onBlur: handleInputBlur,
        onChange: handleInputChange,
        onKeyDown: handleKeyDown,
        value: inputValue,
      }}
      addOnBlur={false}
      values={tags}
      onRemove={handleTagRemove}
    />
  );
};

export default CustomTagInput;
